import React, { useCallback, useMemo } from 'react'
import isEqual from 'react-fast-compare'
import type { CopyMetadataFields } from 'types/copy-metadata'
import TreeItem from 'components/ui/tree-item'

const getValues = (object: Record<string, any>): unknown[] => {
  let result = []

  for (const key in object) {
    if (object.hasOwnProperty(key)) {
      if (typeof object[key] === 'object') {
        result = result.concat(getValues(object[key]))
      } else {
        result.push(object[key])
      }
    }
  }

  return result
}

const updateValues = (
  object: Record<string, any>,
  value: unknown,
): Record<string, any> => {
  let result = {}

  for (const key in object) {
    if (object.hasOwnProperty(key)) {
      if (typeof object[key] === 'object') {
        result[key] = updateValues(object[key], value)
      } else {
        result[key] = value
      }
    }
  }

  return result
}

type Props = {
  caption: React.ReactNode
  data?: Partial<CopyMetadataFields>
  depth: number
  disabled?: boolean
  expanded?: boolean
  id?: string
  onExpand?: (id: string) => void
  onSelect?: (data: Partial<CopyMetadataFields>) => void
}

/**
 * Tree item component for metadata field selection in copy metadata interface
 * Handles hierarchical field selection with checked, partial, and unchecked states
 * @param caption - Display text or element for the field
 * @param data - Metadata field data structure for selection state
 * @param depth - Nesting depth for visual indentation
 * @param disabled - Whether the field is disabled
 * @param expanded - Whether the field is expanded (for expandable items)
 * @param id - Unique identifier for expand/collapse functionality
 * @param onExpand - Callback when field is expanded/collapsed
 * @param onSelect - Callback when field selection state changes
 */
const CopyMetadataField = ({
  caption,
  data,
  depth,
  disabled,
  expanded,
  id,
  onExpand,
  onSelect,
}: Props) => {
  const checked: ('checked' | 'partial') | null | undefined = useMemo(() => {
    if (!data) {
      return undefined
    }

    const values = getValues(data)
    const selectedValues = values.filter(Boolean)

    if (selectedValues.length === values.length) {
      return 'checked'
    }

    if (selectedValues.length > 0) {
      return 'partial'
    }

    return undefined
  }, [data])
  const handleExpand = useCallback(() => {
    if (id && onExpand) {
      onExpand(id)
    }
  }, [id, onExpand])
  const handleSelect = useCallback(() => {
    if (onSelect) {
      onSelect(updateValues(data, checked === 'checked' ? false : true))
    }
  }, [checked, data, onSelect])
  return (
    <TreeItem
      caption={caption}
      checked={checked}
      depth={depth}
      disabled={disabled}
      expandable={Boolean(onExpand)}
      expanded={expanded}
      selectable={Boolean(data && onSelect)}
      onClick={onSelect ? handleSelect : undefined}
      onExpand={onExpand ? handleExpand : undefined}
    />
  )
}

export default React.memo<Props>(CopyMetadataField, isEqual)
